import { useToast } from '../../hooks/useToast';
import { Toast } from '../../components/Toast';
import ContentGrid from '../../components/ContentGrid';
import AppBar from '../../components/AppBar';
import styles from './page.module.css';
import GlobalTabs from '../../components/GlobalTabs';
import GlobalCardRadioButton from '../../components/GlobalCardRadioButton';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppFooter from '../../components/AppFooter';
import DoubleConfirmationModal from '../../components/DoubleConfirmationModal';
import { useTheme } from '../../context/ThemeProvider';
import { auth, db } from '../../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useUserProfile } from '../../hooks/useUserProfile';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import { GlobalInput } from '../../components/GlobalInput';
import { GlobalButton } from '../../components/GlobalButton';
import { ProfileCard } from '../../components/ProfileCard';
import { AVAILABLE_LANGUAGES, LANGUAGE_MAP, getLanguageByCode } from '../../utils/languages';
import { fetchSponsorshipBadge as fetchSponsorshipBadgeController } from './controller';

export default function SettingsPage() {
  const { user, language, loading, firstName, lastName, avatar, role, status, budget, darkMode, company } = useUserProfile();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(LANGUAGE_MAP[language ?? 'en'] ?? 'gb');
  const [showModal, setShowModal] = useState(false);
  const [isSavingLanguage, setIsSavingLanguage] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const [sponsorshipBadge, setSponsorshipBadge] = useState<{enabled: boolean; text: string; logoUrl: string} | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const { isVisible, message, type, hideToast, showToast } = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state synchronized with URL
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl === 'languages' ? 'languages' : 'profile';
  });

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    avatar: avatar || '',
    availableBudget: budget?.['2025']?.availableBudget || 0,
    yearlyBudget: budget?.['2025']?.yearlyBudget || 0,
    darkMode: darkMode,
    email: user?.email || '',
    firstName: firstName || '',
    lastName: lastName || '',
    language: language || 'en'
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    if (language) {
      setSelectedLanguage(LANGUAGE_MAP[language] ?? 'gb');
    }
  }, [language]);

  // Update profile form when user data changes
  useEffect(() => {
    setProfileForm({
      avatar: avatar || '',
      availableBudget: budget?.['2025']?.availableBudget || 0,
      yearlyBudget: budget?.['2025']?.yearlyBudget || 0,
      darkMode: darkMode,
      email: user?.email || '',
      firstName: firstName || '',
      lastName: lastName || '',
      language: language || 'en'
    });
  }, [avatar, budget, darkMode, user?.email, firstName, lastName, language]);

  // Fetch sponsorship badge and company name from user's company
  const fetchSponsorshipBadge = async (userCompanyId: string) => {
    if (!userCompanyId || userCompanyId.trim() === '') {
      setCompanyName('');
      setSponsorshipBadge(null);
      return;
    }
    
    try {
      const { companyName, sponsorshipBadge } = await fetchSponsorshipBadgeController(userCompanyId);
      setCompanyName(companyName || '');
      if (sponsorshipBadge) {
        setSponsorshipBadge(sponsorshipBadge);
      } else {
        setSponsorshipBadge(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching sponsorship badge:', error);
      }
      setCompanyName('');
      setSponsorshipBadge(null);
    }
  };

  // Fetch sponsorship badge when company changes
  useEffect(() => {
    if (company && company.trim() !== '') {
      fetchSponsorshipBadge(company);
    } else {
      setCompanyName('');
      setSponsorshipBadge(null);
    }
  }, [company]);

  // Sync activeTab with URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const newActiveTab = tabFromUrl === 'languages' ? 'languages' : 'profile';
    setActiveTab(newActiveTab);
  }, [searchParams]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [loading, user, navigate]);

  if (loading || (!user && typeof window !== 'undefined')) {
    return null;
  }

  const handleLanguageConfirm = async () => {
    if (!pendingLanguage) return;
    setIsSavingLanguage(true);
    try {
      // Convert flag ID to language code
      const languageConfig = getLanguageByCode(pendingLanguage) || 
        AVAILABLE_LANGUAGES.find(lang => lang.flagId === pendingLanguage);
      
      if (!languageConfig) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Invalid language selection:', pendingLanguage);
      }
        return;
      }

      // Update local state
      setSelectedLanguage(pendingLanguage);

      // Update the language in Firestore
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { language: languageConfig.code });
      }
    } finally {
      setIsSavingLanguage(false);
      setShowModal(false);
      setPendingLanguage(null);
    }
  };

  const handleLanguageCancel = () => {
    setPendingLanguage(null);
    setShowModal(false);
  };

  const handleProfileSave = async () => {
    if (!user?.uid) return;

    setIsSavingProfile(true);
    try {
      const userRef = doc(db, 'users', user.uid);

      // Prepare update data
      const updateData: any = {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        avatar: profileForm.avatar,
        language: profileForm.language
      };

      // Update budget for 2025
      if (profileForm.availableBudget !== undefined || profileForm.yearlyBudget !== undefined) {
        updateData.budget = {
          ...budget,
          '2025': {
            availableBudget: profileForm.availableBudget,
            yearlyBudget: profileForm.yearlyBudget
          }
        };
      }

      await updateDoc(userRef, updateData);

      // Show success message
      showToast(t('settings_profile_updated_success'), 'success');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error updating profile:', error);
      }
      showToast(t('settings_profile_update_error'), 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleProfileFormChange = (field: string, value: any) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
    // Update URL with tab parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (tabName === 'profile') {
      newSearchParams.delete('tab'); // Remove tab param for default tab
    } else {
      newSearchParams.set('tab', tabName);
    }
    setSearchParams(newSearchParams);
  };

  return (
    <div>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows="64px 1fr 48px">

        {/* Toolbar left */}
        <div className={styles.homeToolbarLeft}>

          {/* Back button */}
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('settings_navigate_back')}
              icon={<img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Left" width={8} height={8} />}
              onClick={() => navigate('/home')}
            />
          </div>

          {/* Title */}
          <b>{t('settings_claim_text')}</b>
        </div>

        {/* Toolbar right */}
        <div className={styles.homeToolbarRight}>
        </div>

        {/* Tabs and content */}
        <div className={styles.homeTabs}>
          <GlobalTabs
            initialIndex={activeTab === 'profile' ? 0 : 1}
            onChange={(index) => {
              const tabName = index === 0 ? 'profile' : 'languages';
              handleTabChange(tabName);
            }}
            tabs={[
              {
                label: t('settings_profile_tab'),
                content: (
                  <div className={styles.profileSectionWrapper}>

                    {/* Profile card */}
                    <ProfileCard
                      firstName={firstName}
                      lastName={lastName}
                      email={user?.email || undefined}
                      avatar={avatar}
                      status={status}
                      role={role || "Super admin"}
                      isDarkMode={isDarkMode}
                    />

                    {/* Profile form */}
                    <div className={styles.profileForm}>
                      <div className={styles.formGrid}>

                        {/* Personal information section */}
                        <div className={styles.formSection}>
                          <h4 className={styles.sectionTitle}>{t('settings_personal_information')}</h4>

                          <div className={styles.formRow}>
                            <GlobalInput
                              label={t('settings_first_name')}
                              value={profileForm.firstName}
                              onChange={(e) => handleProfileFormChange('firstName', e.target.value)}
                              className={styles.formInput}
                            />
                            <GlobalInput
                              label={t('settings_email')}
                              value={profileForm.email}
                              onChange={(e) => handleProfileFormChange('email', e.target.value)}
                              className={styles.formInput}
                              disabled={true}
                            />
                          </div>

                          <div className={styles.formRow}>
                            <GlobalInput
                              label={t('settings_last_name')}
                              value={profileForm.lastName}
                              onChange={(e) => handleProfileFormChange('lastName', e.target.value)}
                              className={styles.formInput}
                            />
                          </div>
                        </div>

                        {/* Save button */}
                        <div className={styles.formActions}>
                          <GlobalButton
                            onClick={handleProfileSave}
                            isLoading={isSavingProfile}
                            disabled={isSavingProfile}
                          >
                            {isSavingProfile ? t('settings_saving') : t('settings_save_profile')}
                          </GlobalButton>
                        </div>
                      </div>
                    </div>

                    {/* Separator */}
                    <div className={styles.gridSeparatorWrapper}>
                      <div className={styles.gridSeparator} />
                    </div>

                    {/* Document Preview */}
                    {(companyName || sponsorshipBadge?.logoUrl) && (
                      <div className={styles.sideSection}>
                        <h2>{t('settings_your_company')}</h2>
                        {companyName && (
                          <p dangerouslySetInnerHTML={{ __html: t('settings_company_employee_text').replace('{companyName}', companyName)} }>
                          </p>
                        )}
                        {sponsorshipBadge?.logoUrl && (
                          <div className={styles.sponsorshipSection}>
                            <div className={styles.sponsorLogo}>
                              <img
                                src={sponsorshipBadge.logoUrl}
                                alt="Corporate identity"
                                width={145}
                                height="auto"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                label: t('settings_languages_tab'),
                content: (
                  <div className={styles.languagesSectionWrapper}>

                    {/* Language list */}
                    <div className={styles.languagesWrapper}>
                      {AVAILABLE_LANGUAGES.map((lang) => (
                        <GlobalCardRadioButton
                          key={lang.flagId}
                          isSelected={selectedLanguage === lang.flagId}
                          onSelect={() => {
                            setPendingLanguage(lang.flagId);
                            setShowModal(true);
                          }}
                          name="language"
                          value={lang.flagId}
                          icon={
                            (
                              <img
                                src={`https://cdn.ipregistry.co/flags/emojitwo/${lang.flagId}.svg`}
                                alt={lang.name}
                                className={styles.flagIcon}
                                width={32}
                                height={32}
                              />
                            )
                          }
                          headline={lang.name}
                          paragraph={lang.description}
                          ctaLabel={selectedLanguage === lang.flagId ? '' : t('settings_switch_language')}
                          ctaIcon={<span>→</span>}
                        />
                      ))}
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Footer */}
        <AppFooter inline />
      </ContentGrid>

      {/* Confirmation modal */}
      <DoubleConfirmationModal
        isOpen={showModal}
        title={t('settings_language_confirm_title')}
        paragraph={t('settings_language_confirm_paragraph')}
        primaryLabel={t('settings_confirm_switch')}
        primaryIcon="/assets/icons/arrow.trianglehead.2.counterclockwise.rotate.90.red.svg"
        secondaryLabel={t('settings_cancel')}
        secondaryIcon={isDarkMode ? "/assets/icons/xmark.svg" : "/assets/icons/xmark.black.svg"}
        onPrimary={handleLanguageConfirm}
        onSecondary={handleLanguageCancel}
        primaryDestructive
        loading={isSavingLanguage}
      />

      {/* Toast */}
      <Toast
        isVisible={isVisible}
        message={message || ''}
        type={type}
        onClose={hideToast}
      />
    </div>
  );
}
